import { Fragment, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PDFViewerModal from "@/components/PDFViewerModal";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, X, Save, Mail, DollarSign, Truck, Search, UserPlus, FileText, Warehouse, Download } from "lucide-react";
import StockOrderDialog from "@/components/purchasing/StockOrderDialog";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import { SortableHeader } from "@/components/common/SortHeader";
import { btn, BTN } from "@/components/common/btn";
import { shortDate, money } from "@/lib/format";
import { toast } from "sonner";
import { setHideTax } from "@/lib/api";
import { onlinePaymentsEnabled, emailIntro, viewLabel } from "@/lib/onlinePayments";
import { TaxHiddenBadge } from "@/components/common/TaxHiddenBadge";
import { PartsQuoteGrid } from "@/components/parts/PartsQuoteGrid";
import {
  listPartsOrders, createPartsOrder, updatePartsOrder, deletePartsOrder,
  calculateTax, getSettings, getClients, getSites, addClientContact,
  emailPartsQuote, exportPartsOrderToFoundation, markPartsOrderPaid,
  previewPartsOrderPO, createPartsOrderPO, logPartsOrderPOSent,
} from "@/lib/api";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { PartsStatusButton, PartsShipModal } from "./PartsStatusButton";
import { docFilename } from "@/components/common/EmailDocMode";

// Workflow: open → submitted → paid → shipped (auto-archived)
// cancelled = soft-deleted, shows in History
const STATUS_COLORS = {
  open: "bg-slate-100 text-slate-600",
  submitted: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  shipped: "bg-sky-100 text-sky-700",
  cancelled: "bg-red-100 text-red-700",
};
const STATUS_LABELS = {
  open: "Draft",
  submitted: "Sent",
  paid: "Paid",
  shipped: "Shipped",
  cancelled: "Cancelled",
};
// Default (no pill on) = every open quote (draft + sent + paid). Draft · Sent · Paid narrow it, click again to clear;
// Archived (shipped + cancelled) is the outside view after the divider.
const VIEWS = [
  { key: "open", label: "Draft" },
  { key: "submitted", label: "Sent" },
  { key: "paid", label: "Paid" },
  { key: "shipped", label: "Archived", dividerBefore: true },
];
const SORT_COLS = [
  { key: "order_number", label: "Order #", className: "w-24" },
  { key: "company", label: "Company" },
  { key: "contact", label: "Contact" },
  { key: "total", label: "Total", right: true, className: "w-24" },
  { key: "info", label: "Info", noSort: true, className: "w-28 lg:w-40" },
  { key: "created_at", label: "Date", right: true, className: "w-20" },
  { key: "status", label: "Status", className: "w-28" },
];
const LABEL = "text-[11px] text-slate-500";

const fmt = money;
const formatPhone = (val) => {
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const emptyLine = () => ({ qty: 1, part_name: "", unit_cost: 0, margin_pct: 50, unit_price: 0, total: 0, is_stock: false, vendor: "" });
const emptyOrder = () => ({
  company: "", contact: "", phone: "", email: "",
  client_id: "", site_id: "",
  ship_to_address: "", ship_to_city: "", ship_to_state: "", ship_to_zip: "",
  line_items: [emptyLine()],
  is_taxed: false, hide_tax: false, tax_rate: 0, freight: 0, weight: 0, is_envelope: false, envelope_weight: 0, notes: "",
});

export default function PartsCounter() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [freightTable, setFreightTable] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [shipModalOrder, setShipModalOrder] = useState(null);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [subTab, setSubTab] = useState("invoice");
  const [sort, setSort] = useState({ field: "created_at", dir: "desc" });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientSites, setClientSites] = useState([]);  // sites for the currently selected customer
  // Track whether the PM has manually overridden freight in the current form
  // session. Heavy orders (≥100 lbs) default to $200 + red highlight to prompt
  // a real LTL quote — the warning clears the moment the PM types a sensible
  // value into the Freight field.
  const [freightOverridden, setFreightOverridden] = useState(false);
  // Default CC Fee % from Settings → Default Rates. Drives the in-form
  // quote preview total so the on-screen number matches what the
  // backend computes when the order is saved.
  const [ccFeePct, setCcFeePct] = useState(0);
  const formRef = useRef(null);

  // Click outside form collapses it (only when viewing an existing saved order).
  useEffect(() => {
    if (!form || !editing) return; // Only apply to existing orders (not brand-new)
    const handler = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        // Ignore clicks on toast, modals, and the order row that opened it
        const target = e.target;
        if (target.closest("[data-sonner-toaster]")) return;
        // Ignore clicks inside a dialog/popover layered OVER the form — but not the sheet that contains it
        const dlg = target.closest('[role="dialog"],[role="alertdialog"],[role="listbox"],[data-radix-popper-content-wrapper]');
        if (dlg && !dlg.contains(formRef.current)) return;
        if (target.closest('[data-testid^="order-row-"]')) return;
        if (target.closest('[data-testid="new-order-btn"]')) return;
        setForm(null);
        setEditing(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [form, editing]);

  const load = useCallback(async () => {
    try {
      const [ordRes, settRes, cliRes] = await Promise.all([listPartsOrders(), getSettings(), getClients({ limit: 2000 })]);
      setOrders(ordRes.data);
      setFreightTable(settRes.data?.freight_estimate_table || []);
      // Source of truth for CC fee % — Settings → Default Rates →
      // Default CC Fee %. Falls back to legacy `cc_fee_rate` key, then
      // 0 if neither is set.
      const v = parseFloat(settRes.data?.default_cc_fee_pct ?? settRes.data?.cc_fee_rate);
      setCcFeePct(Number.isFinite(v) ? v : 0);
      // Filter out the Parts Counter placeholder client — pure walk-in bucket
      const realClients = (cliRes.data || []).filter(c => c.customer_type !== "parts_counter");
      setClients(realClients);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Whenever the form's client_id changes, load that client's sites for the Site picker
  useEffect(() => {
    if (!form?.client_id) { setClientSites([]); return; }
    (async () => {
      try {
        const res = await getSites({ client_id: form.client_id });
        const sites = res.data || [];
        setClientSites(sites);
        // Auto-pick the primary site (or the only site) so ship-to fills without an extra click
        if (!form.site_id && sites.length > 0) {
          const primary = sites.find(s => s.is_primary) || (sites.length === 1 ? sites[0] : null);
          if (primary) {
            setForm(p => ({
              ...p,
              site_id: primary.id,
              ship_to_address: p.ship_to_address || primary.address || "",
              ship_to_city: p.ship_to_city || primary.city || "",
              ship_to_state: p.ship_to_state || primary.state || "",
              ship_to_zip: p.ship_to_zip || primary.zip_code || "",
            }));
          }
        }
      } catch { setClientSites([]); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.client_id]);

  const currentClient = useMemo(
    () => clients.find(c => c.id === form?.client_id) || null,
    [clients, form?.client_id]
  );
  const currentClientContacts = useMemo(
    () => (currentClient?.contacts || []).filter(c => c.email || c.phone || c.name),
    [currentClient]
  );

  const filtered = orders.filter(o => {
    // Global search across identity fields
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = [
        o.order_number, o.company, o.contact, o.email, o.phone,
        o.ship_to_city, o.ship_to_state, o.ship_to_zip, o.ship_to_address,
        o.tracking_number, o.tracking_carrier, o.foundation_invoice_number,
        ...(o.line_items || []).map(li => li.part_name),
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filter === "open") return o.status === "open";
    if (filter === "submitted") return o.status === "submitted";
    if (filter === "paid") return o.status === "paid";
    if (filter === "shipped") return o.status === "shipped" || o.status === "cancelled";
    return o.status !== "shipped" && o.status !== "cancelled"; // default: every open quote
  });
  const toggleSort = (field) => setSort((s) => (s.field === field
    ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
    : { field, dir: field === "created_at" || field === "total" ? "desc" : "asc" }));
  const sortedRows = [...filtered].sort((a, b) => {
    const va = a[sort.field], vb = b[sort.field];
    let c;
    if (sort.field === "total") c = (va || 0) - (vb || 0);
    else if (sort.field === "created_at") c = new Date(va || 0) - new Date(vb || 0);
    else if (sort.field === "status") c = (STATUS_LABELS[va] || "").localeCompare(STATUS_LABELS[vb] || "");
    else c = String(va || "").localeCompare(String(vb || ""), undefined, { sensitivity: "base" });
    return sort.dir === "asc" ? c : -c;
  });
  const listTotal = filtered.reduce((s, o) => s + (o.total || 0), 0);

  const updateLineItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      // Auto-calc unit_price from unit_cost + margin_pct (GM-style, matches proposals/WO)
      if (field === "unit_cost" || field === "margin_pct") {
        const cost = parseFloat(items[idx].unit_cost) || 0;
        const margin = parseFloat(items[idx].margin_pct) || 0;
        const unit = margin < 100 && margin >= 0 ? round2(cost / (1 - margin / 100)) : cost;
        items[idx].unit_price = unit;
      }
      // If PM overrides unit_price directly, back-compute margin
      if (field === "unit_price") {
        const cost = parseFloat(items[idx].unit_cost) || 0;
        const unit = parseFloat(items[idx].unit_price) || 0;
        if (unit > 0 && cost > 0 && cost < unit) {
          items[idx].margin_pct = round2((1 - cost / unit) * 100);
        }
      }
      if (field === "qty" || field === "unit_price" || field === "unit_cost" || field === "margin_pct") {
        items[idx].total = round2((parseFloat(items[idx].qty) || 0) * (parseFloat(items[idx].unit_price) || 0));
      }
      return { ...prev, line_items: items };
    });
  };

  const calcFreight = useCallback((weight) => {
    const w = parseFloat(weight) || 0;
    // Heavy orders (≥100 lbs) need a real LTL/freight quote. Default to $200
    // so the PM has a placeholder, and pair with red-highlight UI to flag
    // that they should call the carrier and override.
    if (w >= 100) return 200;
    if (w <= 0 || freightTable.length === 0) return 0;
    const bracket = freightTable.find(b => w <= b.max_weight);
    return bracket ? bracket.rate : freightTable[freightTable.length - 1]?.rate || 0;
  }, [freightTable]);

  // True when the current order is heavy AND the PM hasn't typed in their own
  // freight number yet. Drives the red border + helper text on the Freight input.
  const heavyFreightWarning = !!form && !form.is_envelope && (parseFloat(form.weight) || 0) >= 100 && !freightOverridden;

  const lookupTax = useCallback(async (city, state) => {
    if (!state) return 0;
    try {
      const res = await calculateTax({ city, state });
      if (res.data.zip) setForm(prev => ({ ...prev, ship_to_zip: res.data.zip }));
      return res.data.tax_rate || 0;
    } catch { return 0; }
  }, []);

  const handleShipToChange = async (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "ship_to_city" || field === "ship_to_state") {
      const city = field === "ship_to_city" ? value : form.ship_to_city;
      const state = field === "ship_to_state" ? value : form.ship_to_state;
      if (state) {
        const rate = await lookupTax(city, state);
        setForm(prev => ({ ...prev, [field]: value, is_taxed: rate > 0, tax_rate: rate }));
      }
    }
  };

  const handleWeightChange = (value) => {
    const w = parseFloat(value) || 0;
    // Reset the manual override whenever the weight changes — a new weight
    // means a new freight context, so the heavy-order auto default reapplies.
    setFreightOverridden(false);
    setForm(prev => ({ ...prev, weight: w, freight: calcFreight(w) }));
  };

  const handleEnvelopeChange = (checked) => {
    // Envelope = small parcel, never heavy. Clear the override flag either way.
    setFreightOverridden(false);
    if (checked) {
      const envRows = freightTable.filter(b => (b.label || "").toLowerCase().includes("envelope")).sort((a, b) => a.max_weight - b.max_weight);
      const first = envRows[0];
      setForm(prev => ({ ...prev, is_envelope: true, envelope_weight: first?.max_weight || 0, weight: 0, freight: first?.rate || 0 }));
    } else {
      setForm(prev => ({ ...prev, is_envelope: false, envelope_weight: 0, freight: 0 }));
    }
  };

  const handleEnvelopeWeightChange = (val) => {
    const w = parseFloat(val);
    const envRow = freightTable.find(b => (b.label || "").toLowerCase().includes("envelope") && b.max_weight === w);
    setForm(prev => ({ ...prev, envelope_weight: w, freight: envRow?.rate || 0 }));
  };

  const envelopeOptions = freightTable.filter(b => (b.label || "").toLowerCase().includes("envelope")).sort((a, b) => a.max_weight - b.max_weight);

  // iter310 — display only. The order is always taxed at its ship-to
  // jurisdiction; this only decides whether the QUOTE mentions tax.
  const handleHideTaxChange = async (checked) => {
    setForm(prev => ({ ...prev, hide_tax: !!checked }));
    if (form?.id) {
      try {
        await setHideTax("parts_order", form.id, !!checked);
        toast.success(checked ? "Tax hidden on this quote" : "Tax will show on this quote");
      } catch (err) {
        setForm(prev => ({ ...prev, hide_tax: !checked }));
        toast.error(err.response?.data?.detail || "Could not change how tax is shown");
      }
    }
  };

  // CC fee is baked into customer-facing total. Source = Settings →
  // Default Rates → Default CC Fee % (set 0 to disable globally).
  const CC_FEE_RATE = ccFeePct;
  const subtotal = form ? form.line_items.reduce((s, li) => s + (li.total || 0), 0) : 0;
  const taxAmt = form?.is_taxed ? round2(subtotal * ((form.tax_rate || 0) / 100)) : 0;
  const freightAmt = parseFloat(form?.freight) || 0;
  const preFee = round2(subtotal + taxAmt + freightAmt);
  const ccFee = round2(preFee * (CC_FEE_RATE / 100));
  const total = round2(preFee + ccFee);

  const handleSave = async () => {
    if (!form.company.trim()) { toast.error("Company name required"); return; }
    if (form.line_items.every(li => !li.part_name.trim())) { toast.error("Add at least one part"); return; }
    try {
      const payload = { ...form, line_items: form.line_items.filter(li => li.part_name.trim()) };
      let saved;
      if (editing) {
        const res = await updatePartsOrder(editing, payload);
        saved = res.data;
        toast.success("Quote updated");
      } else {
        const res = await createPartsOrder(payload);
        saved = res.data;
        toast.success("Quote created");
        // Stay on the form — switch to edit mode so the user can click Email Quote immediately
        setEditing(saved.id);
      }
      await load();
      return saved;
    } catch { toast.error("Failed to save"); return null; }
  };

  // ── Email Quote: fires the PartsStatusButton email flow (save + email)
  const handleSaveAndEmail = async () => {
    const saved = await handleSave();
    if (!saved) return;
    // Trigger the same email flow that PartsStatusButton uses via the API.
    // We call emailPartsQuote directly (rather than going through the button)
    // since the form may have just saved a fresh order with zero flow state.
    if (!saved.email) { toast.error("Customer email required"); return; }
    if ((saved.total || 0) <= 0) { toast.error("Quote total must be greater than $0"); return; }
    try {
      const res = await emailPartsQuote(saved.id);
      const { short_code, order_number } = res.data;
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const quoteUrl = `${baseUrl}/api/r/q/${short_code}/${docFilename("Parts_Quote", saved?.quote_number || saved?.order_number || saved?.id)}`;
      const payOn = res.data?.payments_enabled ?? await onlinePaymentsEnabled();
      const subject = encodeURIComponent(`Parts Quote ${order_number} — ${saved.company}`);
      const body = encodeURIComponent(
        `Hi ${saved.contact || "there"},\n\n` +
        `${emailIntro("parts quote", payOn)}\n\n` +
        `Quote ${order_number}\n` +
        `${viewLabel(payOn)}: ${quoteUrl}\n\n` +
        `Total: ${fmt(saved.total)}\n\n` +
        `Once payment is received we will ship your parts.\n\n` +
        `Thank you,\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
      );
      window.location.href = `mailto:${saved.email}?subject=${subject}&body=${body}`;
      toast.success("Opening email — status advanced to Sent");
      await load();
    } catch {
      toast.error("Failed to prepare quote email");
    }
  };

  const handleFoundationExport = async (order) => {
    setBusyId(order.id);
    try {
      const res = await exportPartsOrderToFoundation(order.id);
      const { foundation_invoice_number } = res.data;
      toast.success(`Foundation Invoice ${foundation_invoice_number} generated`);
      downloadFoundationCSV(res.data, order);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to export");
    } finally { setBusyId(null); }
  };

  // Shared PDF viewer state — used for both the Quote/Invoice and Vendor PO
  // popups. `kind` distinguishes the title label; `url` points at the backend
  // rendering endpoint.
  const [pdfView, setPdfView] = useState(null); // { url, title }

  const handleViewPDF = (order) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const isPaidLike = order.status === "paid" || order.status === "shipped";
    setPdfView({
      url: `${baseUrl}/api/parts-orders/${order.id}/pdf`,
      title: `${isPaidLike ? "Invoice" : "Quote"} ${order.order_number}`,
    });
  };

  const handleDelete = (order) => setCancelTarget(order);
  const confirmCancel = async () => {
    const order = cancelTarget;
    if (!order) return;
    setCancelTarget(null);
    try {
      await deletePartsOrder(order.id);
      toast.success(`${order.order_number} cancelled — moved to Archived`);
      // If the order was open in the inline form, close it
      if (editing === order.id) { setForm(null); setEditing(null); }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel order");
    }
  };

  // Row-level "Mark Paid" for submitted orders — advances to paid; auto-jumps
  // the list filter to the Paid tab so the user sees the order in its new home.
  const handleRowMarkPaid = async (order) => {
    setBusyId(order.id);
    try {
      await markPartsOrderPaid(order.id);
      toast.success(`${order.order_number} marked Paid`);
      setFilter("paid");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    } finally { setBusyId(null); }
  };

  const openEdit = (order) => {
    // Always open inline — never navigate away. Paid/shipped load read-only.
    setEditing(order.id);
    setSubTab("invoice");
    // Treat the saved freight as manually overridden when we re-open an
    // existing order — the PM already chose this number; we shouldn't show
    // the red "needs override" warning unless they reset the weight.
    setFreightOverridden(true);
    setForm({
      company: order.company || "", contact: order.contact || "",
      phone: order.phone || "", email: order.email || "",
      client_id: order.client_id || "", site_id: order.site_id || "",
      ship_to_address: order.ship_to_address || "", ship_to_city: order.ship_to_city || "",
      ship_to_state: order.ship_to_state || "", ship_to_zip: order.ship_to_zip || "",
      line_items: order.line_items?.length
        ? order.line_items.map(li => ({
            // #250 (iter168): spread the line item FIRST so persisted
            // fields like `is_stock` and `vendor` survive the re-open
            // round-trip. The explicit overrides below normalise the
            // numeric fields without dropping anything we didn't
            // explicitly name.
            ...li,
            qty: li.qty || 1,
            part_name: li.part_name || "",
            unit_cost: li.unit_cost ?? 0,
            margin_pct: li.margin_pct ?? 50,
            unit_price: li.unit_price || 0,
            total: li.total || 0,
            cost_type: li.cost_type || "2",
            is_stock: li.is_stock || false,
            vendor: li.vendor || "",
          }))
        : [emptyLine()],
      is_taxed: order.is_taxed || false, hide_tax: !!order.hide_tax,
      tax_rate: order.tax_rate || 0, freight: order.freight || 0,
      weight: order.weight || 0, is_envelope: order.is_envelope || false, envelope_weight: order.envelope_weight || 0, notes: order.notes || "",
    });
  };

  if (loading) return <p className="py-10 text-center text-sm text-slate-400">Loading...</p>;

  const openForm = (next, id) => { setForm(next); setEditing(id); setSubTab("invoice"); };

  return (
    <div className="h-full flex flex-col" data-testid="parts-counter">
      {/* Toolbar — search far left · view pills · actions · count far right (the sheet header carries the name) */}
      <div className="flex flex-wrap items-center gap-2 px-1 pb-3 border-b border-slate-200" data-testid="parts-counter-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="search-parts" width="w-40 md:w-56 xl:w-72" />
        <PillRail testId="parts-counter-views">
          {VIEWS.map((v) => (
            <Fragment key={v.key}>
              {v.dividerBefore && <PillDivider />}
              <Pill on={filter === v.key} testId={`filter-${v.key}`}
                onClick={() => { setFilter(filter === v.key ? "" : v.key); setForm(null); setEditing(null); }}>
                {v.label}
              </Pill>
            </Fragment>
          ))}
        </PillRail>
        {!form && (
          <button type="button" className={btn("write")} aria-label="New Quote" title="New Quote"
            onClick={() => { openForm(emptyOrder(), null); setFreightOverridden(false); }} data-testid="new-order-btn">
            <Plus className="w-4 h-4" />
          </button>
        )}
        {!form && (
          <button type="button" className={btn("mode")} onClick={() => setStockDialogOpen(true)} title="ABS Stock order (warehouse restock PO)" data-testid="abs-stock-order-btn">
            <Warehouse className="w-3.5 h-3.5" /> Stock
          </button>
        )}
        <div className="ml-auto text-xs text-slate-500 num" data-testid="parts-counter-count">
          {filtered.length} quote{filtered.length === 1 ? "" : "s"} · {money(listTotal)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {/* Order Form */}
        {form && (() => {
          const currentOrder = editing ? orders.find(o => o.id === editing) : null;
          const readOnly = !!(currentOrder && (currentOrder.status === "paid" || currentOrder.status === "shipped"));
          return (
          <div ref={formRef} className="border border-slate-200 rounded-lg p-5 bg-white space-y-4" data-testid="order-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                {!editing && "New Quote"}
                {editing && !readOnly && <>Edit Quote <span className="font-mono text-brand">{currentOrder?.order_number}</span></>}
                {editing && readOnly && <>{currentOrder?.status === "shipped" ? "Shipped" : "Paid"} <span className="font-mono text-brand">{currentOrder?.order_number}</span> <span className="font-normal text-slate-500">· view only</span></>}
              </h3>
              <button type="button" aria-label="Close" onClick={() => { setForm(null); setEditing(null); }} className="text-slate-400 hover:text-slate-700" data-testid="order-form-close"><X className="w-4 h-4" /></button>
            </div>
            {/* Foundation tagging — every parts counter transaction rolls up under
                customer "Parts" (#222) when exported to Foundation. Read-only. */}
            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div>
                <Label className={LABEL}>Foundation Customer</Label>
                <Input
                  value="Parts"
                  disabled
                  readOnly
                  className="h-8 text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                  data-testid="foundation-customer-locked"
                />
              </div>
              <div>
                <Label className={LABEL}>Foundation Customer #</Label>
                <Input
                  value="222"
                  disabled
                  readOnly
                  className="h-8 text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                  data-testid="foundation-customer-number-locked"
                />
              </div>
            </div>
            {/* Customer info — Company autocomplete + Contact picker + Email/Phone. Paid/shipped orders are locked. */}
            <fieldset disabled={readOnly} className="min-w-0 space-y-4 disabled:opacity-80" data-testid="order-form-customer">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className={LABEL}>Company *</Label>
                <CompanyAutocomplete
                  value={form.company}
                  clients={clients}
                  clientId={form.client_id}
                  onTextChange={(v) => setForm(p => ({ ...p, company: v, client_id: "", site_id: "" }))}
                  onClientSelect={(c) => {
                    // Pre-fill from client's primary contact + primary site
                    const primary = (c.contacts || []).find(x => x.is_primary) || (c.contacts || [])[0];
                    setForm(p => ({
                      ...p,
                      company: c.name,
                      client_id: c.id,
                      site_id: "",
                      contact: primary?.name || p.contact,
                      phone: primary?.phone ? formatPhone(primary.phone) : p.phone,
                      email: primary?.email || p.email,
                      // Enriched client has primary-site address flattened onto it
                      ship_to_address: c.address || p.ship_to_address,
                      ship_to_city: c.city || p.ship_to_city,
                      ship_to_state: c.state || p.ship_to_state,
                      ship_to_zip: c.zip_code || p.ship_to_zip,
                    }));
                  }}
                  onClear={() => setForm(p => ({ ...p, company: "", client_id: "", site_id: "" }))}
                />
              </div>
              <div>
                <Label className={LABEL}>Contact</Label>
                <ContactPicker
                  value={form.contact}
                  clientId={form.client_id}
                  contacts={currentClientContacts}
                  onFreeText={(v) => setForm(p => ({ ...p, contact: v }))}
                  onContactSelect={(c) => setForm(p => ({
                    ...p,
                    contact: c.name,
                    email: c.email || p.email,
                    phone: c.phone ? formatPhone(c.phone) : p.phone,
                  }))}
                  onContactAdded={(c) => {
                    // Also merge into the loaded clients cache so the picker sees it
                    setClients(prev => prev.map(cl => cl.id === form.client_id
                      ? { ...cl, contacts: [...(cl.contacts || []), c] }
                      : cl));
                    setForm(p => ({
                      ...p,
                      contact: c.name,
                      email: c.email || p.email,
                      phone: c.phone ? formatPhone(c.phone) : p.phone,
                    }));
                  }}
                />
              </div>
              <div>
                <Label className={LABEL}>Phone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: formatPhone(e.target.value) }))} className="h-8 text-sm" placeholder="(303) 333-3333" data-testid="order-phone" />
              </div>
              <div>
                <Label className={LABEL}>Email *</Label>
                <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="h-8 text-sm" placeholder="required to email quote" data-testid="order-email" />
              </div>
            </div>

            {/* Site picker — only when existing customer is selected + has sites */}
            {form.client_id && clientSites.length > 0 && (
              <div>
                <Label className={LABEL}>Site</Label>
                <Select
                  value={form.site_id || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") { setForm(p => ({ ...p, site_id: "" })); return; }
                    const site = clientSites.find(s => s.id === v);
                    if (!site) return;
                    setForm(p => ({
                      ...p,
                      site_id: site.id,
                      ship_to_address: site.address || "",
                      ship_to_city: site.city || "",
                      ship_to_state: site.state || "",
                      ship_to_zip: site.zip_code || "",
                    }));
                    // Re-run tax lookup for the new site
                    if (site.city || site.state) {
                      (async () => {
                        try {
                          const res = await calculateTax({ city: site.city, state: site.state });
                          setForm(p => ({ ...p, is_taxed: (res.data.tax_rate || 0) > 0, tax_rate: res.data.tax_rate || 0 }));
                        } catch { /* ignore */ }
                      })();
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-sm mt-1" data-testid="select-parts-site"><SelectValue placeholder="Select site (auto-fills ship-to)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None (manual ship-to) —</SelectItem>
                    {clientSites.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.address ? ` — ${[s.address, s.city, s.state].filter(Boolean).join(", ")}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Ship To */}
            <div>
              <Label className={LABEL}>Ship To</Label>
              <div className="grid grid-cols-5 gap-3 mt-1">
                <div className="col-span-2">
                  <Input value={form.ship_to_address} onChange={e => handleShipToChange("ship_to_address", e.target.value)} placeholder="Address" className="h-8 text-sm" data-testid="ship-address" />
                </div>
                <div>
                  <Input value={form.ship_to_city} onChange={e => handleShipToChange("ship_to_city", e.target.value)} placeholder="City" className="h-8 text-sm" data-testid="ship-city" />
                </div>
                <div>
                  <Input value={form.ship_to_state} onChange={e => handleShipToChange("ship_to_state", e.target.value.toUpperCase())} placeholder="ST" className="h-8 text-sm" maxLength={2} data-testid="ship-state" />
                </div>
                <div>
                  <Input value={form.ship_to_zip} onChange={e => handleShipToChange("ship_to_zip", e.target.value)} placeholder="ZIP" className="h-8 text-sm" data-testid="ship-zip" />
                </div>
              </div>
            </div>
            </fieldset>
            {/* ── Invoice vs Purchase Order sub-tabs (kit pills) ────────────
                Invoice: full quote/invoice editor. Purchase Order: non-stock
                lines grouped by vendor + global PO# + send-to-vendor mailto. */}
            <div className="w-full" data-testid="parts-counter-subtabs">
              <PillRail>
                <Pill on={subTab === "invoice"} onClick={() => setSubTab("invoice")} testId="subtab-invoice">Invoice</Pill>
                <Pill on={subTab === "purchase-order"} onClick={() => setSubTab("purchase-order")} testId="subtab-purchase-order">Purchase Order</Pill>
              </PillRail>

              {subTab === "invoice" && <div className="mt-4 space-y-4">
            <fieldset disabled={readOnly} className="min-w-0 space-y-4 disabled:opacity-80" data-testid="order-form-lines">
            {/* Line items — proposal-style quote grid (new line lands on top; Enter / arrows move through cells). */}
            <PartsQuoteGrid
              lines={form.line_items}
              updateLineItem={updateLineItem}
              addLine={() => setForm(p => ({ ...p, line_items: [emptyLine(), ...p.line_items] }))}
              removeLine={(idx) => setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }))}
              freight={form.freight || 0} taxAmt={form.is_taxed ? taxAmt : 0} taxRate={form.tax_rate} total={total} readOnly={readOnly}
            />
            {/* Tax / Freight / Totals */}
            <div className="flex flex-wrap items-start justify-between gap-4 border-t border-slate-200 pt-3">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox id="hide-tax" checked={!!form.hide_tax} onCheckedChange={handleHideTaxChange} data-testid="hide-tax" />
                    <Label htmlFor="hide-tax" className="text-xs text-slate-600"
                           title="The quote shows no tax — it is still charged inside the total and remitted.">Hide Tax</Label>
                  </div>
                  {form.hide_tax && (
                    <TaxHiddenBadge taxAmount={taxAmt} taxRate={form.tax_rate} testId="parts-tax-hidden-badge" />
                  )}
                  {form.is_taxed && form.tax_rate > 0 && (
                    <span className="text-[11px] text-slate-500">{form.tax_rate}% auto-calculated</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="envelope" checked={form.is_envelope} onCheckedChange={handleEnvelopeChange} data-testid="order-envelope" />
                    <Label htmlFor="envelope" className="text-xs text-slate-600">Envelope</Label>
                  </div>
                  {form.is_envelope && envelopeOptions.length > 0 && (
                    <Select value={String(form.envelope_weight || envelopeOptions[0]?.max_weight)} onValueChange={handleEnvelopeWeightChange}>
                      <SelectTrigger className="h-7 w-48 text-xs" data-testid="envelope-weight-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {envelopeOptions.map(opt => (
                          <SelectItem key={opt.max_weight} value={String(opt.max_weight)}>Up to {opt.max_weight} lb — ${opt.rate}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {!form.is_envelope && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-600 whitespace-nowrap">Weight (lbs)</Label>
                      <Input type="number" value={form.weight || ""} onChange={e => handleWeightChange(e.target.value)} className="h-7 w-20 text-xs" placeholder="0" data-testid="order-weight" />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className={`text-xs ${heavyFreightWarning ? "text-red-600 font-semibold" : "text-slate-600"}`}>Freight</Label>
                    <Input
                      type="number"
                      value={form.freight || ""}
                      onChange={e => {
                        // Mark the freight as manually overridden so the heavy-
                        // order red highlight clears as soon as the PM commits a
                        // value — even if they happen to type the auto default.
                        setFreightOverridden(true);
                        setForm(p => ({ ...p, freight: parseFloat(e.target.value) || 0 }));
                      }}
                      className={`h-7 w-20 text-xs ${heavyFreightWarning ? "border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500" : ""}`}
                      data-testid="order-freight"
                    />
                  </div>
                  {heavyFreightWarning && (
                    <span className="text-[10px] text-red-600 font-semibold" data-testid="freight-heavy-warning">
                      ≥100 lbs — get LTL quote &amp; override
                    </span>
                  )}
                  {form.is_envelope && <span className="text-[11px] text-slate-400 whitespace-nowrap">envelope rate from settings</span>}
                  {!form.is_envelope && form.weight > 0 && form.weight < 100 && <span className="text-[11px] text-slate-400 whitespace-nowrap">est. from weight table</span>}
                </div>
              </div>
              <div className="ml-auto text-right min-w-[220px] bg-slate-50 rounded-md p-3 space-y-1">
                <div className="text-xs text-slate-600 flex justify-between gap-6"><span>Subtotal</span><span className="num font-medium">{fmt(subtotal)}</span></div>
                {form.is_taxed && (
                  <div className="text-xs text-slate-600 flex justify-between gap-6"><span>Tax ({form.tax_rate}%)</span><span className="num font-medium">{fmt(taxAmt)}</span></div>
                )}
                {(form.freight || 0) > 0 && (
                  <div className="text-xs text-slate-600 flex justify-between gap-6"><span>Freight</span><span className="num font-medium">{fmt(form.freight)}</span></div>
                )}
                <div className="text-xs text-slate-600 flex justify-between gap-6 border-t border-slate-200 pt-1"><span>CC Fee ({CC_FEE_RATE}%)</span><span className="num font-medium">{fmt(ccFee)}</span></div>
                <div className="text-sm text-slate-900 flex justify-between gap-6 border-t border-slate-300 pt-1 font-semibold"><span>Total</span><span className="num">{fmt(total)}</span></div>
                <div className="text-[11px] text-slate-400 pt-1">Customer sees Total only — tax, freight &amp; CC fee included.</div>
              </div>
            </div>
            </fieldset>
            <div className="flex gap-2 items-center">
              {!readOnly && (
                <>
                  <button type="button" className={btn("write")} onClick={handleSave} data-testid="save-order">
                    <Save className="w-3.5 h-3.5" /> Save Quote
                  </button>
                  <button type="button" className={btn("data")} onClick={handleSaveAndEmail} data-testid="form-email-quote" title="Save, then open the quote email — status advances to Sent">
                    <Mail className="w-3.5 h-3.5" /> Email Quote
                  </button>
                </>
              )}
              {/* View Invoice — opens the quote/invoice PDF. Same doc acts as quote
                  (pre-payment) or invoice (post-payment); label follows status. */}
              {editing && currentOrder && (
                <button
                  type="button"
                  className={btn("nav")}
                  onClick={() => handleViewPDF(currentOrder)}
                  data-testid="view-invoice-pdf"
                  title="Open the quote/invoice PDF"
                >
                  <FileText className="w-3.5 h-3.5" /> View {currentOrder.status === "paid" || currentOrder.status === "shipped" ? "Invoice" : "Quote"}
                </button>
              )}
              {/* Status-advance button visible once the order is saved (editing mode) */}
              {editing && currentOrder && (
                <PartsStatusButton order={currentOrder} onChanged={load} />
              )}
              {/* Manual Mark Paid — for cash/check/offline payments, overrides status. Only shown on open/submitted. */}
              {editing && currentOrder && (currentOrder.status === "open" || currentOrder.status === "submitted") && (
                <button
                  type="button"
                  className={btn("write")}
                  disabled={busyId === editing}
                  onClick={async () => {
                    setBusyId(editing);
                    try {
                      await markPartsOrderPaid(editing);
                      toast.success(`${currentOrder.order_number} marked Paid`);
                      setFilter("paid");
                      await load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed to mark paid");
                    } finally {
                      setBusyId(null);
                    }
                  }}
                  data-testid="mark-paid-btn"
                  title="Override — mark this order paid (cash/check/offline)"
                >
                  <DollarSign className="w-3.5 h-3.5" /> Mark Paid
                </button>
              )}
              <button type="button" className={`${btn("nav")} ml-auto`} onClick={() => { setForm(null); setEditing(null); }} data-testid="order-form-close-btn">Close</button>
            </div>
              </div>}

              {/* ── Purchase Order sub-tab ───────────────────────────────── */}
              {subTab === "purchase-order" && <div className="mt-4">
                <PartsOrderPOTab
                  order={currentOrder}
                  editing={editing}
                  onChanged={load}
                  onOpenPDF={(url, title) => setPdfView({ url, title })}
                />
              </div>}
            </div>
          </div>
          );
        })()}

        {/* Orders List */}
        {filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="parts-counter-empty">
            {filter === "open" && "No draft quotes."}
            {filter === "submitted" && "No sent quotes awaiting payment."}
            {filter === "paid" && "No paid orders awaiting shipment."}
            {filter === "shipped" && "Nothing archived yet."}
            {filter === "" && "No open quotes."}
          </p>
        ) : (
          <div className="data-table-container rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              {SORT_COLS.map((c) => (
                <TableHead key={c.key} className={`text-xs whitespace-nowrap ${c.className || ""} ${c.right ? "text-right" : ""}`}>
                  {c.noSort ? <span className="font-semibold">{c.label}</span> : (
                    <SortableHeader label={c.label} sorted={sort.field === c.key} direction={sort.dir} onSort={() => toggleSort(c.key)}
                      testId={`parts-sort-${c.key}`} className={c.right ? "w-full justify-end" : ""} />
                  )}
                </TableHead>
              ))}
              <TableHead className="text-right pr-4 w-16"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sortedRows.map(order => (
                <TableRow key={order.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`order-row-${order.id}`} onClick={() => openEdit(order)}>
                  <TableCell className="text-xs font-mono font-semibold text-brand">{order.order_number}</TableCell>
                  <TableCell className="text-sm font-medium">{order.company}</TableCell>
                  <TableCell className="text-sm text-slate-500">{order.contact || "—"}</TableCell>
                  <TableCell className="text-sm font-medium num">{fmt(order.total)}</TableCell>
                  <TableCell>
                    {order.paid_at && (
                      <div className="text-[11px] text-emerald-700 tabular-nums">Paid {shortDate(order.paid_at)}</div>
                    )}
                    {order.tracking_number && (
                      <div className="text-[11px] text-slate-500">{order.tracking_carrier} · {order.tracking_number}</div>
                    )}
                    {order.picked_up && (
                      <div className="text-[11px] text-slate-500">Picked up at counter</div>
                    )}
                    {order.foundation_invoice_number && (
                      <div className="text-[11px] text-slate-500">Foundation {order.foundation_invoice_number}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 num">{shortDate(order.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`inline-flex min-w-[5.5rem] justify-center whitespace-nowrap border-0 text-[10px] ${STATUS_COLORS[order.status] || STATUS_COLORS.open}`} data-testid={`status-badge-${order.id}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end items-center" onClick={e => e.stopPropagation()}>
                      {/* Mark Paid — only for submitted orders (moves to To Ship) */}
                      {order.status === "submitted" && (
                        <button
                          type="button"
                          className={btn("write")}
                          disabled={busyId === order.id}
                          onClick={() => handleRowMarkPaid(order)}
                          data-testid={`row-mark-paid-${order.id}`}
                          title="Mark as paid — moves to Paid"
                        >
                          <DollarSign className="w-3 h-3" /><span className="hidden lg:inline">Mark Paid</span>
                        </button>
                      )}
                      {/* Shipped — only for paid orders (opens tracking + email, moves to History) */}
                      {order.status === "paid" && (
                        <button
                          type="button"
                          className={btn("write")}
                          onClick={() => setShipModalOrder(order)}
                          data-testid={`row-ship-${order.id}`}
                          title="Add tracking + email customer — moves to Archived"
                        >
                          <Truck className="w-3 h-3" /><span className="hidden lg:inline">Shipped</span>
                        </button>
                      )}
                      {/* Foundation export — only for paid/shipped, optional side-action */}
                      {(order.status === "paid" || order.status === "shipped") && !order.foundation_invoice_number && (
                        <button type="button" className={btn("data")} disabled={busyId === order.id} onClick={() => handleFoundationExport(order)} data-testid={`foundation-${order.id}`} title="Export to Foundation (downloads the import file)">
                          <Download className="w-3 h-3" /><span className="hidden lg:inline">Foundation</span>
                        </button>
                      )}
                      {/* Cancel — only on open/submitted (unpaid) */}
                      {(order.status === "open" || order.status === "submitted") && (
                        <button type="button" aria-label="Cancel quote" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => handleDelete(order)} data-testid={`delete-${order.id}`} title="Cancel quote — moves to Archived">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <AlertDialogContent data-testid="cancel-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {cancelTarget?.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>The quote moves to Archived — you can still open it there.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-order-keep">Keep</AlertDialogCancel>
            <AlertDialogAction className={BTN.danger} onClick={confirmCancel} data-testid="cancel-order-confirm">Yes, Cancel Quote</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Row-triggered ship modal (tracking + email → history) */}
      {shipModalOrder && (
        <PartsShipModal
          open={!!shipModalOrder}
          onOpenChange={(v) => { if (!v) setShipModalOrder(null); }}
          order={shipModalOrder}
          onShipped={async () => { setShipModalOrder(null); await load(); }}
        />
      )}
      {/* Shared in-app PDF viewer — used for the Quote/Invoice AND the Vendor PO
          popups so users never have to jump to a new tab. */}
      <PDFViewerModal
        open={!!pdfView}
        onClose={() => setPdfView(null)}
        url={pdfView?.url}
        title={pdfView?.title}
      />
      {/* ABS Stock (warehouse restock) PO dialog */}
      <StockOrderDialog open={stockDialogOpen} onOpenChange={setStockDialogOpen} />
    </div>
  );
}

function round2(v) { return Math.round((v + Number.EPSILON) * 100) / 100; }

// ──────────────────────────────────────────────────────────────
// Company autocomplete — free-text input with a searchable client dropdown.
// - Typing filters matching existing clients and shows suggestions.
// - Clicking a suggestion promotes the order to "linked customer" mode.
// - Typing anything not in the list keeps the freeform (walk-in) path.
// ──────────────────────────────────────────────────────────────
function CompanyAutocomplete({ value, clients, clientId, onTextChange, onClientSelect, onClear }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const q = (value || "").trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return clients.slice(0, 8);
    return clients.filter(c => (c.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [q, clients]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onTextChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search existing or type walk-in name"
          className="h-8 text-sm pr-7"
          data-testid="order-company"
        />
        {clientId ? (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            title="Linked customer — click to unlink"
            data-testid="company-unlink"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <Search className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        )}
      </div>
      {clientId && (
        <div className="text-[11px] text-emerald-700 mt-0.5">Linked to customer record</div>
      )}
      {open && !clientId && matches.length > 0 && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="company-suggestions">
          {matches.map(c => {
            const addr = [c.city, c.state].filter(Boolean).join(", ");
            const contactCount = (c.contacts || []).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { onClientSelect(c); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 border-slate-100"
                data-testid={`company-suggestion-${c.id}`}
              >
                <div className="text-sm font-medium text-slate-800">{c.name}</div>
                <div className="text-[11px] text-slate-500 flex gap-2">
                  {addr && <span>{addr}</span>}
                  {contactCount > 0 && <span>· {contactCount} contact{contactCount === 1 ? "" : "s"}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Contact picker — dropdown of existing customer contacts + inline "Add New".
// Falls back to a plain text input when no customer is linked.
// ──────────────────────────────────────────────────────────────
function ContactPicker({ value, clientId, contacts, onFreeText, onContactSelect, onContactAdded }) {
  const [mode, setMode] = useState("pick"); // "pick" | "add"
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);

  // If no linked customer, fall back to a plain freeform input
  if (!clientId) {
    return <Input value={value} onChange={e => onFreeText(e.target.value)} className="h-8 text-sm" data-testid="order-contact" />;
  }

  const selected = contacts.find(c => c.name === value || c.email === value);

  const saveNew = async () => {
    if (!newName.trim() && !newEmail.trim()) { toast.error("Name or email required"); return; }
    setSaving(true);
    try {
      const payload = { name: newName.trim() || newEmail.trim(), email: newEmail.trim(), phone: newPhone.trim() };
      const res = await addClientContact(clientId, payload);
      const c = res.data?.contact || res.data || payload;
      toast.success("Contact saved to customer & site");
      setNewName(""); setNewEmail(""); setNewPhone("");
      setMode("pick");
      onContactAdded(c);
    } catch {
      toast.error("Failed to save contact");
    } finally { setSaving(false); }
  };

  if (mode === "add") {
    return (
      <div className="space-y-1.5 border rounded-md p-2 bg-slate-50" data-testid="contact-add-inline">
        <div className="flex gap-1.5">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" className="h-7 text-xs" data-testid="contact-add-name" />
          <Input value={newPhone} onChange={e => setNewPhone(formatPhone(e.target.value))} placeholder="Phone" className="h-7 text-xs" data-testid="contact-add-phone" />
        </div>
        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" className="h-7 text-xs" type="email" data-testid="contact-add-email" />
        <div className="flex gap-1.5">
          <button type="button" className={`${btn("write")} flex-1`} onClick={saveNew} disabled={saving} data-testid="contact-add-save">
            <UserPlus className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
          </button>
          <button type="button" className={btn("nav")} onClick={() => setMode("pick")} data-testid="contact-add-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <Select
      value={selected ? `c:${selected.id || selected.email}` : "__none__"}
      onValueChange={(v) => {
        if (v === "__add__") { setMode("add"); return; }
        if (v === "__none__") { onFreeText(""); return; }
        const key = v.slice(2);
        const c = contacts.find(x => (x.id || x.email) === key);
        if (c) onContactSelect(c);
      }}
    >
      <SelectTrigger className="h-8 text-sm" data-testid="order-contact-select">
        <SelectValue placeholder={contacts.length ? "Select contact" : "No saved contacts"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— None —</SelectItem>
        {contacts.map(c => (
          <SelectItem key={c.id || c.email} value={`c:${c.id || c.email}`}>
            {c.name}{c.email ? ` — ${c.email}` : ""}{c.is_primary ? " (Primary)" : ""}
          </SelectItem>
        ))}
        <SelectItem value="__add__" className="text-brand font-medium">+ Add New Contact…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function downloadFoundationCSV(data, order) {
  const { general, line_details, foundation_invoice_number } = data;
  const rows = [
    ["Invoice No", "Customer No", "Job No", "Description", "Date", "Tax No", "Amount", "Tax", "Terms", "Payment Method"].join(","),
    [
      general.invoice_no, general.customer_no, general.job_no,
      `"${(general.description || "").replace(/"/g, '""')}"`,
      general.invoice_date, general.tax_no,
      general.invoice_amount, general.tax_amount, general.terms,
      `"${general.payment_method || ''}"`,
    ].join(","),
    "",
    ["Line", "Date", "Item", "Description", "UM", "Qty", "Unit Price", "Extended", "GL Income", "GL Desc"].join(","),
    ...line_details.map(l => [
      l.line_no, l.date, l.item_no || "",
      `"${(l.description || "").replace(/"/g, '""')}"`,
      l.um, l.quantity, l.unit_price, l.extended_price, l.gl_income, `"${l.gl_income_desc}"`,
    ].join(",")),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Foundation_${foundation_invoice_number}_${order.order_number}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}



// ─────────────────────────────────────────────────────────────────────────────
// PartsOrderPOTab
// Purchase Order sub-tab rendered inside a Parts Counter order. Shows non-stock
// lines grouped by vendor, lets the PM reserve a global PO number, and fires a
// mailto per vendor so the vendor AP/billing contact receives their portion of
// the order. Stock lines are excluded entirely (they ship from inventory).
// ─────────────────────────────────────────────────────────────────────────────
function PartsOrderPOTab({ order, editing, onChanged, onOpenPDF }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendorEmails, setVendorEmails] = useState({}); // {vendor: email}

  const reload = useCallback(async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const r = await previewPartsOrderPO(editing);
      setPreview(r.data);
      // Auto-reserve a PO number the FIRST time the tab sees non-stock lines.
      // The manual "Reserve PO" step is gone — if there's anything to order,
      // we need the PO# immediately so the user can email vendors straight
      // away with no extra clicks.
      if (!r.data.existing_po_number && (r.data.groups || []).length > 0) {
        try {
          const cr = await createPartsOrderPO(editing);
          setPreview(p => ({ ...(p || r.data), existing_po_number: cr.data.po_number, short_code: cr.data.short_code || p?.short_code }));
          onChanged?.();
        } catch {
          /* non-fatal — backend may have rejected due to all-stock etc. */
        }
      }
    } catch {
      toast.error("Failed to load PO preview");
    }
    setLoading(false);
  }, [editing, onChanged]);

  useEffect(() => { reload(); }, [reload, order?.updated_at]);

  // Not yet saved — can't build a PO until the order has an ID
  if (!editing || !order) {
    return (
      <p className="py-10 text-center text-sm text-slate-400" data-testid="po-tab-unsaved">Save the quote first, then return here to build a vendor PO.</p>
    );
  }

  if (loading) {
    return <p className="py-10 text-center text-sm text-slate-400">Loading vendor breakdown…</p>;
  }

  const groups = preview?.groups || [];
  const poNumber = preview?.existing_po_number || order.po_number;
  const shortCode = preview?.short_code || order.short_code || "";

  const poPdfUrl = (vendor) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const v = encodeURIComponent(vendor);
    if (shortCode) return `${base}/api/r/po/${shortCode}?vendor=${v}`;
    return `${base}/api/parts-orders/${editing}/po-pdf?vendor=${v}`;
  };

  const handleViewVendorPO = (g) => {
    if (!poNumber) { toast.error("Reserve a PO number first"); return; }
    onOpenPDF?.(poPdfUrl(g.vendor), `PO ${poNumber} — ${g.vendor}`);
  };

  const handleSendVendor = async (g) => {
    const email = (vendorEmails[g.vendor] || "").trim();
    if (!email) { toast.error("Enter vendor AP/billing email first"); return; }
    if (!poNumber) { toast.error("Create a PO number first"); return; }
    const subject = encodeURIComponent(`Purchase Order ${poNumber} — ${g.vendor}`);
    const partsLines = g.line_items.map(li =>
      `  • ${li.qty}× ${li.part_name || "(unnamed)"} @ ${fmt(li.unit_cost || 0)} ea`
    ).join("\n");
    const pdfLink = poPdfUrl(g.vendor);
    const body = encodeURIComponent(
      `Hello ${g.vendor},\n\n` +
      `Please find our purchase order details below and view the PDF at the link provided.\n\n` +
      `PO Number: ${poNumber}\n` +
      `View PO: ${pdfLink}\n\n` +
      `Ship To: ${order.ship_to_address || ""}, ${order.ship_to_city || ""}, ${order.ship_to_state || ""} ${order.ship_to_zip || ""}\n\n` +
      `Items:\n${partsLines}\n\n` +
      `Vendor Cost Total: ${fmt(g.total_cost)}\n\n` +
      `Please confirm receipt and provide shipping/tracking details at your earliest convenience.\n\n` +
      `Thank you,\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
    );
    try { await logPartsOrderPOSent(editing, { vendor: g.vendor, email }); } catch { /* non-fatal */ }
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    toast.success(`PO email prepared for ${g.vendor}`);
    await reload();
    await onChanged?.();
  };

  if (groups.length === 0) {
    return (
      <div className="py-10 text-center text-slate-400" data-testid="po-tab-empty">
        <p className="text-sm font-medium">No vendor PO needed</p>
        <p className="text-xs mt-1">Every line on this order is flagged <span className="font-mono bg-slate-200 px-1.5 py-0.5 rounded">Stock</span>, or no lines have been entered yet.</p>
        <p className="text-xs mt-1">Uncheck <em>Stock</em> on any line to order it from a vendor.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="po-tab-content">
      {/* PO Header — PO# auto-reserved on tab load; no manual button needed */}
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <div className="text-[11px] text-slate-500 font-semibold">Vendor Purchase Order</div>
          {poNumber ? (
            <div className="text-lg font-mono font-semibold text-brand" data-testid="po-number">{poNumber}</div>
          ) : (
            <div className="text-sm text-slate-400">Preparing PO number…</div>
          )}
        </div>
        {poNumber && (
          <div className="text-[11px] text-slate-500 text-right leading-tight">
            Reserved on save<br />
            <span className="text-slate-400">One PO# per order</span>
          </div>
        )}
      </div>

      {/* Per-vendor groupings */}
      {groups.map((g, gi) => (
        <div key={`vendor-${g.vendor}`} className="rounded-lg border border-slate-200 bg-white" data-testid={`po-vendor-group-${g.vendor}`}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
            <div>
              <div className="text-[11px] text-slate-500 font-semibold">Vendor</div>
              <div className="text-sm font-semibold text-slate-800">{g.vendor}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-slate-500 font-semibold">Cost</div>
              <div className="text-sm font-semibold text-slate-800 num">{fmt(g.total_cost)}</div>
            </div>
          </div>
          <div className="px-4 py-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold w-16">Qty</TableHead>
                  <TableHead className="text-xs font-semibold">Part Name</TableHead>
                  <TableHead className="text-xs font-semibold text-right w-24">Unit Cost</TableHead>
                  <TableHead className="text-xs font-semibold text-right w-24">Line Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.line_items.map((li, li_i) => (
                  // eslint-disable-next-line react/no-array-index-key -- vendor PO line items have no stable id at this draft stage
                  <TableRow key={`vli-${li_i}-${li.part_name || ''}`}>
                    <TableCell className="text-sm">{li.qty}</TableCell>
                    <TableCell className="text-sm">{li.part_name || <span className="text-slate-400">(unnamed)</span>}</TableCell>
                    <TableCell className="text-sm num">{fmt(li.unit_cost || 0)}</TableCell>
                    <TableCell className="text-sm num font-medium">{fmt((li.qty || 0) * (li.unit_cost || 0))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200">
            <Label className="text-xs text-slate-600 whitespace-nowrap">Vendor AP Email</Label>
            <Input
              type="email"
              placeholder="ap@vendor.com"
              value={vendorEmails[g.vendor] || ""}
              onChange={e => setVendorEmails(p => ({ ...p, [g.vendor]: e.target.value }))}
              className="h-8 text-sm flex-1 max-w-xs"
              data-testid={`vendor-email-${gi}`}
            />
            <button
              type="button"
              className={btn("nav")}
              disabled={!poNumber}
              onClick={() => handleViewVendorPO(g)}
              title={!poNumber ? "Reserve PO# first" : "Open this vendor's PO PDF"}
              data-testid={`view-vendor-po-${gi}`}
            >
              <FileText className="w-3.5 h-3.5" /> View PO
            </button>
            <button
              type="button"
              className={btn("data")}
              disabled={!poNumber || !(vendorEmails[g.vendor] || "").trim()}
              onClick={() => handleSendVendor(g)}
              title={!poNumber ? "Reserve PO# first" : ""}
              data-testid={`send-vendor-po-${gi}`}
            >
              <Mail className="w-3.5 h-3.5" /> Send PO
            </button>
          </div>
        </div>
      ))}

      {/* Sent history */}
      {(order.po_vendor_emails || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[11px] text-slate-500 font-semibold mb-2">Sent History</div>
          <div className="space-y-1">
            {order.po_vendor_emails.map((h) => (
              <div key={`sent-${h.sent_at}-${h.vendor}`} className="text-xs text-slate-600 flex items-center gap-3">
                <span className="num text-slate-400">{shortDate(h.sent_at)} {new Date(h.sent_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                <span className="font-medium text-slate-700">{h.vendor}</span>
                <span className="text-slate-500">→ {h.email}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}